import { Component, ErrorInfo, ReactNode } from 'react';
import { analyticsService } from '@/lib/analytics';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * ErrorBoundary component that catches JavaScript errors anywhere in the child
 * component tree, logs those errors, and displays a fallback UI.
 * Also integrates with Firebase Analytics for error tracking.
 */
export class AnalyticsErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console
    console.error('Error caught by boundary:', error, errorInfo);
    
    // Track error in analytics
    analyticsService.trackError(error, 'error_boundary');
    
    // Update state with error info
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    // Track retry attempt
    analyticsService.trackEvent('error_boundary_retry');
    
    // Reset error state
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Oops! Something went wrong</AlertTitle>
              <AlertDescription className="mt-2">
                We apologize for the inconvenience. An unexpected error occurred.
                {import.meta.env.DEV && this.state.error && (
                  <details className="mt-2 text-xs">
                    <summary className="cursor-pointer">Error Details (Dev Mode)</summary>
                    <pre className="mt-1 whitespace-pre-wrap">
                      {this.state.error.message}
                      {this.state.error.stack}
                    </pre>
                  </details>
                )}
              </AlertDescription>
            </Alert>
            
            <div className="mt-4 flex gap-2">
              <Button onClick={this.handleRetry} variant="outline" className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Try Again
              </Button>
              
              <Button onClick={() => window.location.href = '/'} variant="default">
                Go Home
              </Button>
            </div>
            
            {import.meta.env.DEV && (
              <div className="mt-4 p-2 bg-muted rounded text-xs text-muted-foreground">
                <strong>Development Mode:</strong> This error has been logged to analytics.
                Check the console for more details.
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AnalyticsErrorBoundary;
